import styled from 'styled-components';
import { getColorVariantFromBackground } from '@klv/shared/utils/functions';
import Button from '@klv/atoms/button';
import Typography from '@klv/atoms/typography';
import Image from '@klv/atoms/image';
import type { TColorVariant, TCtaVariant, TTileHover } from '@klv/shared/styles/types';

export const HOHeadline = styled.div`
  max-width: 77%;
  word-wrap: break-word;
  ${({ theme }) => `
    ${theme.screenSize.s576.mediaQuery} {
      max-width: 76%;
    }
    ${theme.screenSize.s768.mediaQuery} {
      max-width: 67%
    }
    ${theme.screenSize.s1024.mediaQuery} {
      max-width: 78%
    }
    ${theme.screenSize.s1200.mediaQuery} {
      max-width: 77%;
    }
  `}
`;

export const HPHeadline = styled(HOHeadline)`
  margin-bottom: .8rem;
  ${({ theme }) => `
    ${theme.screenSize.s768.mediaQuery} {
      max-width: 88%
    }
    ${theme.screenSize.s1024.mediaQuery} {
      max-width: 78%
    }
    ${theme.screenSize.s1200.mediaQuery} {
      max-width: 77%;
    }
    ${theme.screenSize.s1366.mediaQuery} {
      max-width: 85%;
    }
  `}
`;

export const HPParagraph = styled.p`
  width: 100%; 
  margin-bottom: .8rem;
`;

export const IconContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: end;
  color: inherit;
`;

export const TileLink = styled.a`
  text-decoration: none;
  z-index: 1;

  &::after {
    border-radius: .8rem;
    bottom: 0;
    content: '';
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
  }
`;

export const StyledButton = styled.div`
  display: inline-block;
  z-index: 2;
  position: relative;

  cursor: pointer;
  display: inline-block;
  border-radius: 3.2rem;
  border-width: 2px;
  border-style: solid;
  text-decoration: none;
  font-size: 1.6rem;
  line-height: 2.0rem;
  padding: 1.2rem 3rem;
  font-weight: 500;
  transition: all .3s ease;
  background-repeat: no-repeat;
  background-image: linear-gradient(currentColor, currentColor);
  background-size: 100% 2px;
  background-position: left 100%;
  border-radius: 0;
  border-style: none;
  padding: .2rem 0;

  &:active {
    opacity: 1;
  }
`;


export const HOContainer = styled.div<{ $backgroundColor: TColorVariant; $hoverColor: TTileHover; }>`
  background-color: ${({ $backgroundColor, theme }) => theme.tileList[$backgroundColor].tileColor};
  border-radius: 1.2rem;
  cursor: pointer;
  grid-column: 1/-1;
  padding: 3rem 2.4rem;
  width: 100%;
  position: relative;
  transition: ${({ theme }) => `background ${theme.tileList.timing} ease-in-out`};
  &:hover,
  &:focus,
  &:focus-within {
    ${({ $hoverColor, $backgroundColor, theme }) => `
      color: ${['lavender', 'sage'].includes($hoverColor) ? 'var(--color-white)' : 'var(--color-core-charcoal)'};
      fill: ${['lavender', 'sage'].includes($hoverColor) ? 'var(--color-white)' : 'var(--color-core-charcoal)'};
      background-color: ${theme.tileList[$backgroundColor].tileHover[$hoverColor]};
    `}
  }
  ${({ theme }) => `
    ${theme.screenSize.s576.mediaQuery} {
      grid-column: span 6;
    }
    ${theme.screenSize.s1024.mediaQuery} {
      grid-column: span 4;
    }
  `}
  
  ${TileLink} {
    &:focus-visible {
      &::after {
        outline: ${({ $backgroundColor, theme }) => `${theme.outline.colors[getColorVariantFromBackground($backgroundColor)].color} ${theme.outline.global.size} ${theme.outline.global.style}`};
        outline-offset: 3px;
      }
    }
  }
`;

export const HPContainer = styled(HOContainer)`
  border-radius: .8rem;
  padding: 2.4rem;
`;

export const IOContainer = styled(HOContainer)`
  border-radius: .8rem;
  grid-column: span 12;
  ${({ theme }) => `
    ${theme.screenSize.s768.mediaQuery} {
      grid-column: span 6;
    }
    ${theme.screenSize.s1024.mediaQuery} {
      grid-column: span 4;
    }
  `}
`;

export const StyledImage = styled(Image)`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  align-self: center;

  img {
    object-fit: contain !important;
    object-position: left;
  }
`;

export const ImageContainer = styled.div<{ $ctaVariant: TCtaVariant; }>`
  display: flex;
  height: 4.5rem;
  ${({ theme, $ctaVariant }) => `
  ${$ctaVariant === 'tertiary button' ? (`
      width: 21rem;
      margin: 0rem 2rem  0rem 0rem ;
      ${theme.screenSize.s576.mediaQuery} {
        margin: 0rem 17.6rem 0rem  0rem ;
      }
      ${theme.screenSize.s768.mediaQuery} {
        margin: 0rem  0rem  2rem 0rem ;
      }
      ${theme.screenSize.s1200.mediaQuery} {
        margin: 0 2rem 0 0;
      }
      ${theme.screenSize.s1366.mediaQuery} {
        margin: 0 4.8rem 0 0;
      }
    `) : 'width: 26.8rem;'}
    }
  `}
`;

export const ContentContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: space-between;
  align-items: center;
`;

export const IOContentContainer = styled(ContentContainer)<{ $ctaVariant: TCtaVariant; }>`
  ${({ theme, $ctaVariant }) => `
  ${$ctaVariant === 'tertiary button' ? (`
      ${theme.screenSize.s768.mediaQuery} {
        flex-direction: column;
        align-items: flex-start;
        min-height: 10rem;
      }
      ${theme.screenSize.s1200.mediaQuery} {
        flex-direction: row;
        align-items: center;
        min-height: auto;
      }
    `) : ''}
  `}
`;
export const HPContentContainer = styled.div`
`;
